import type { Recipe } from "@/types/recipe"

export const recipes: ReadonlyArray<Recipe> = [
  {
    id: "recipe-cucumbers-classic-1",
    slug: "marinovani-ogirky-na-zymu-klasychni",
    title: "Мариновані огірки на зиму — класичний рецепт",
    excerpt:
      "Хрусткі мариновані огірки з часником, кропом і збалансованим маринадом для домашніх заготовок на зиму.",
    description:
      "Перевірений рецепт маринованих огірків на зиму для тих, хто хоче отримати хрусткий результат, чистий смак і надійну домашню консервацію без зайвих експериментів.",
    image: "/images/recipes/cucumbers-classic.jpg",
    category: "cucumbers",
    tags: ["мариновані", "на зиму", "класика", "хрусткі"],
    season: ["summer", "autumn"],
    difficulty: "easy",
    servings: 3,
    prepTimeMinutes: 30,
    cookTimeMinutes: 20,
    totalTimeMinutes: 50,
    storageInfo: "Зберігати у прохолодному темному місці до 12 місяців.",
    safetyNotes: [
      "Використовуйте тільки чисті простерилізовані банки та кришки.",
      "Якщо кришка здулася або розсіл став мутним і має неприємний запах, заготовку вживати не можна.",
    ],
    ingredients: [
      { name: "Огірки", amount: "1.8 кг" },
      { name: "Вода", amount: "1.5 л" },
      { name: "Сіль", amount: "2 ст. л." },
      { name: "Цукор", amount: "3 ст. л." },
      { name: "Оцет", amount: "100 мл", notes: "9%" },
      { name: "Часник", amount: "6 зубчиків" },
      { name: "Кріп", amount: "3 парасольки" },
      { name: "Листя смородини", amount: "6 шт." },
    ],
    steps: [
      {
        title: "Підготуйте огірки",
        description:
          "Замочіть огірки в холодній воді на 2–3 години, а потім добре промийте. Це допоможе зробити їх щільнішими й хрусткішими.",
      },
      {
        title: "Підготуйте банки",
        description:
          "Простерилізуйте банки та кришки. На дно кожної банки покладіть кріп, листя смородини та очищений часник.",
      },
      {
        title: "Укладіть огірки",
        description:
          "Щільно складіть огірки в банки, чергуючи більші та менші плоди, щоб заповнити простір рівномірно.",
      },
      {
        title: "Зваріть маринад",
        description:
          "Доведіть воду до кипіння, додайте сіль і цукор, добре розчиніть їх, а наприкінці влийте оцет.",
        timerMinutes: 5,
      },
      {
        title: "Залийте та закатайте",
        description:
          "Залийте банки гарячим маринадом, накрийте кришками та герметично закатайте. Дайте охолонути в перевернутому вигляді.",
      },
    ],
    kuhlykTip:
      "Для більш хрустких огірків не пропускайте замочування й обирайте щільні свіжі плоди без пошкоджень.",
    products: [
      {
        title: "Банки для консервації",
        href: "/products",
        affiliate: false,
      },
      {
        title: "Кришки та ключі",
        href: "/products",
        affiliate: false,
      },
      {
        title: "Лійки та кухонні дрібниці",
        href: "/products",
        affiliate: false,
      },
    ],
    featured: true,
    publishedAt: "2026-03-10",
    updatedAt: "2026-03-12",
    authorName: "Кухлик",
  },
  {
    id: "recipe-tomatoes-sweet-1",
    slug: "solodki-pomidory-na-zymu",
    title: "Солодкі помідори на зиму в маринаді",
    excerpt:
      "Ароматні мариновані помідори з м’яким солодкуватим смаком і чистим збалансованим маринадом.",
    description:
      "Домашній рецепт солодких помідорів на зиму для тих, хто любить ніжний смак, помірну кислотність і акуратну текстуру без зайвої різкості.",
    image: "/images/recipes/tomatoes-sweet.jpg",
    category: "tomatoes",
    tags: ["помідори", "солодкі", "мариновані", "на зиму"],
    season: ["summer", "autumn"],
    difficulty: "easy",
    servings: 2,
    prepTimeMinutes: 25,
    cookTimeMinutes: 20,
    totalTimeMinutes: 45,
    storageInfo: "Зберігати в темному прохолодному місці до 10–12 місяців.",
    safetyNotes: [
      "Не використовуйте перезрілі, тріснуті або пошкоджені помідори.",
      "Слідкуйте, щоб банки були чистими, а помідори не мали ознак псування перед закруткою.",
    ],
    ingredients: [
      { name: "Помідори", amount: "1.5 кг" },
      { name: "Вода", amount: "1 л" },
      { name: "Сіль", amount: "1 ст. л." },
      { name: "Цукор", amount: "3 ст. л." },
      { name: "Оцет", amount: "50 мл", notes: "9%" },
      { name: "Часник", amount: "4 зубчики" },
      { name: "Перець горошком", amount: "8 шт." },
      { name: "Лавровий лист", amount: "2 шт." },
    ],
    steps: [
      {
        title: "Підготуйте помідори",
        description:
          "Добре промийте помідори та проколіть кожен біля плодоніжки зубочисткою, щоб шкірка менше тріскалася під час заливання.",
      },
      {
        title: "Складіть у банки",
        description:
          "У стерильні банки покладіть часник, перець, лавровий лист і щільно, але акуратно, укладіть помідори.",
      },
      {
        title: "Зробіть перший залив",
        description:
          "Залийте банки окропом, накрийте кришками й залиште на 10 хвилин. Потім злийте воду в каструлю.",
        timerMinutes: 10,
      },
      {
        title: "Приготуйте маринад",
        description:
          "Додайте до злитої води сіль і цукор, прокип’ятіть 3–4 хвилини, а наприкінці влийте оцет.",
        timerMinutes: 4,
      },
      {
        title: "Закрийте банки",
        description:
          "Повторно залийте помідори маринадом, герметично закрийте кришками та дайте заготовці повністю охолонути.",
      },
    ],
    kuhlykTip:
      "Для цього рецепта добре підходять невеликі щільні помідори — вони краще тримають форму й виглядають акуратно в банці.",
    products: [
      {
        title: "Банки та кришки",
        href: "/products",
        affiliate: false,
      },
      {
        title: "Ваги та мірні інструменти",
        href: "/products",
        affiliate: false,
      },
    ],
    featured: true,
    publishedAt: "2026-03-10",
    updatedAt: "2026-03-12",
    authorName: "Кухлик",
  },
  {
    id: "recipe-zucchini-salad-1",
    slug: "kabachkovyi-salat-na-zymu",
    title: "Кабачковий салат на зиму",
    excerpt:
      "Насичений овочевий салат із кабачків, моркви та цибулі — зручна універсальна заготовка для повсякденного столу.",
    description:
      "Практичний рецепт кабачкового салату на зиму для тих, хто хоче використати сезонні овочі й отримати готову ароматну закуску або гарнір у банці.",
    image: "/images/recipes/zucchini-salad.jpg",
    category: "zucchini",
    tags: ["кабачки", "салат", "на зиму", "овочеві заготовки"],
    season: ["summer", "autumn"],
    difficulty: "medium",
    servings: 4,
    prepTimeMinutes: 35,
    cookTimeMinutes: 35,
    totalTimeMinutes: 70,
    storageInfo: "Зберігати у прохолодному місці до 8–10 місяців.",
    safetyNotes: [
      "Для великої партії салату не скорочуйте час термічної обробки.",
      "Не використовуйте овочі з ознаками псування або бродіння.",
    ],
    ingredients: [
      { name: "Кабачки", amount: "2 кг" },
      { name: "Морква", amount: "500 г" },
      { name: "Цибуля", amount: "500 г" },
      { name: "Томатний соус", amount: "500 мл" },
      { name: "Сіль", amount: "2 ст. л." },
      { name: "Цукор", amount: "4 ст. л." },
      { name: "Олія", amount: "150 мл" },
      { name: "Оцет", amount: "80 мл", notes: "9%" },
    ],
    steps: [
      {
        title: "Підготуйте овочі",
        description:
          "Наріжте кабачки кубиком, моркву натріть на великій тертці, а цибулю наріжте півкільцями.",
      },
      {
        title: "Зробіть основу",
        description:
          "У великій каструлі змішайте томатний соус, сіль, цукор і олію, щоб отримати рівномірну ароматну основу.",
      },
      {
        title: "Додайте овочі",
        description:
          "Викладіть овочі в каструлю й тушкуйте до м’якості, періодично помішуючи, щоб салат прогрівався рівномірно.",
        timerMinutes: 25,
      },
      {
        title: "Додайте оцет",
        description:
          "За 5 хвилин до завершення приготування влийте оцет і добре перемішайте масу.",
        timerMinutes: 5,
      },
      {
        title: "Розкладіть у банки",
        description:
          "Гарячий салат розкладіть у стерильні банки, щільно закрийте й дайте повністю охолонути.",
      },
    ],
    kuhlykTip:
      "Для більш виразного смаку можна додати трохи паприки, часнику або суміші перців наприкінці тушкування.",
    products: [
      {
        title: "Банки для салатів і закусок",
        href: "/products",
        affiliate: false,
      },
      {
        title: "Кухонні ножі та дошки",
        href: "/products",
        affiliate: false,
      },
    ],
    featured: false,
    publishedAt: "2026-03-11",
    updatedAt: "2026-03-12",
    authorName: "Кухлик",
  },
  {
    id: "recipe-fermented-cabbage-1",
    slug: "kvashena-kapusta-klasychna",
    title: "Квашена капуста — класичний домашній варіант",
    excerpt:
      "Простий рецепт квашеної капусти без оцту — натуральна ферментація, традиційний смак і мінімум інгредієнтів.",
    description:
      "Класичний домашній рецепт квашеної капусти для тих, хто хоче освоїти базову ферментацію, отримати природний смак і зробити корисну заготовку без оцту.",
    image: "/images/recipes/fermented-cabbage.jpg",
    category: "fermentation",
    tags: ["ферментація", "квашення", "без оцту", "капуста"],
    season: ["autumn", "winter"],
    difficulty: "easy",
    servings: 6,
    prepTimeMinutes: 30,
    cookTimeMinutes: 0,
    totalTimeMinutes: 30,
    storageInfo:
      "Після завершення ферментації зберігати в холодильнику або льосі до кількох місяців.",
    safetyNotes: [
      "Капуста повинна бути повністю покрита власним соком або розсолом.",
      "Не використовуйте алюмінієвий посуд для ферментації.",
    ],
    ingredients: [
      { name: "Капуста", amount: "2 кг" },
      { name: "Морква", amount: "200 г" },
      { name: "Сіль", amount: "40 г" },
      { name: "Кмин", amount: "1 ч. л.", notes: "за бажанням" },
    ],
    steps: [
      {
        title: "Нашинкуйте овочі",
        description:
          "Тонко нашинкуйте капусту, а моркву натріть на великій тертці.",
      },
      {
        title: "Перетріть із сіллю",
        description:
          "Змішайте капусту із сіллю та добре перетріть руками, щоб вона пустила достатньо соку.",
      },
      {
        title: "Укладіть у ємність",
        description:
          "Щільно утрамбуйте капусту в банку або керамічну ємність, щоб зверху з’явився сік.",
      },
      {
        title: "Ферментуйте",
        description:
          "Залиште капусту при кімнатній температурі на 3–5 днів, щодня проколюючи масу дерев’яною шпажкою для виходу газів.",
        timerMinutes: 5,
      },
      {
        title: "Перенесіть у холод",
        description:
          "Коли смак стане приємно кислим, перенесіть капусту в холодне місце для подальшого зберігання.",
      },
    ],
    kuhlykTip:
      "Сіль для ферментації краще зважувати, а не відміряти ложками — так смак і стабільність результату будуть точнішими.",
    products: [
      {
        title: "Ваги та мірні інструменти",
        href: "/products",
        affiliate: false,
      },
      {
        title: "Ємності для ферментації",
        href: "/products",
        affiliate: false,
      },
    ],
    featured: true,
    publishedAt: "2026-03-09",
    updatedAt: "2026-03-12",
    authorName: "Кухлик",
  },
  {
    id: "recipe-jam-strawberry-1",
    slug: "polunyczne-varennya-pyatykhvylynka",
    title: "Полуничне варення-п’ятихвилинка",
    excerpt:
      "Швидке полуничне варення з яскравим ароматом ягід і короткою термічною обробкою.",
    description:
      "Зручний рецепт полуничного варення-п’ятихвилинки для тих, хто хоче зберегти смак свіжої ягоди, не витрачати багато часу й отримати гарну домашню заготовку.",
    image: "/images/recipes/strawberry-jam.jpg",
    category: "jam",
    tags: ["варення", "полуниця", "швидко", "ягоди"],
    season: ["summer"],
    difficulty: "easy",
    servings: 4,
    prepTimeMinutes: 20,
    cookTimeMinutes: 15,
    totalTimeMinutes: 35,
    storageInfo: "Зберігати у прохолодному темному місці до 8–10 місяців.",
    safetyNotes: [
      "Для довшого зберігання використовуйте тільки сухі стерильні банки.",
    ],
    ingredients: [
      { name: "Полуниця", amount: "1 кг" },
      { name: "Цукор", amount: "700–800 г" },
      { name: "Лимонний сік", amount: "1 ст. л." },
    ],
    steps: [
      {
        title: "Підготуйте ягоди",
        description:
          "Промийте полуницю, видаліть хвостики, пересипте ягоди цукром і залиште на 2–3 години, щоб вони пустили сік.",
      },
      {
        title: "Доведіть до кипіння",
        description:
          "Поставте ягоди на середній вогонь і обережно доведіть до кипіння, не перемішуючи занадто активно.",
      },
      {
        title: "Проваріть",
        description:
          "Після закипання варіть 5 хвилин, акуратно знімаючи піну.",
        timerMinutes: 5,
      },
      {
        title: "Додайте лимонний сік",
        description:
          "Наприкінці влийте лимонний сік — він освіжить смак і допоможе краще зберегти колір варення.",
      },
      {
        title: "Розлийте в банки",
        description:
          "Розлийте гаряче варення в стерильні банки та щільно закрийте кришками.",
      },
    ],
    kuhlykTip:
      "Не переварюйте полуницю: для варення-п’ятихвилинки особливо важливо зберегти свіжий аромат і ніжну ягідну текстуру.",
    products: [
      {
        title: "Банки для варення",
        href: "/products",
        affiliate: false,
      },
      {
        title: "Ваги та мірні інструменти",
        href: "/products",
        affiliate: false,
      },
    ],
    featured: true,
    publishedAt: "2026-03-08",
    updatedAt: "2026-03-12",
    authorName: "Кухлик",
  },
  {
    id: "recipe-compote-apples-1",
    slug: "yabluchnyi-kompot-na-zymu",
    title: "Яблучний компот на зиму",
    excerpt:
      "Простий домашній компот із яблук, який легко готувати великими партіями для всієї родини.",
    description:
      "Базовий рецепт яблучного компоту на зиму з мінімумом інгредієнтів, м’яким збалансованим смаком і зручним процесом приготування.",
    image: "/images/recipes/apple-compote.jpg",
    category: "compote",
    tags: ["компот", "яблука", "на зиму", "просто"],
    season: ["summer", "autumn"],
    difficulty: "easy",
    servings: 3,
    prepTimeMinutes: 20,
    cookTimeMinutes: 20,
    totalTimeMinutes: 40,
    storageInfo: "Зберігати у темному прохолодному місці до 12 місяців.",
    safetyNotes: [
      "Не використовуйте зіпсовані, підгнилі або занадто м’які яблука.",
    ],
    ingredients: [
      { name: "Яблука", amount: "1 кг" },
      { name: "Вода", amount: "2.5 л" },
      { name: "Цукор", amount: "250–300 г" },
      { name: "Лимонна кислота", amount: "дрібка", notes: "за бажанням" },
    ],
    steps: [
      {
        title: "Підготуйте яблука",
        description:
          "Помийте яблука, видаліть серцевину й наріжте їх часточками середнього розміру.",
      },
      {
        title: "Наповніть банки",
        description:
          "Розкладіть яблука по стерильних банках приблизно на третину об’єму.",
      },
      {
        title: "Приготуйте сироп",
        description:
          "Закип’ятіть воду з цукром, а за бажанням додайте дрібку лимонної кислоти для більш яскравого смаку.",
      },
      {
        title: "Залийте банки",
        description:
          "Залийте яблука киплячим сиропом і накрийте банки кришками.",
      },
      {
        title: "Герметично закрийте",
        description:
          "Закатайте банки, переверніть їх догори дном і залиште до повного охолодження.",
      },
    ],
    kuhlykTip:
      "Для більш насиченого смаку можна поєднати яблука з грушею, сливами або додати маленький шматочок кориці.",
    products: [
      {
        title: "Банки та кришки",
        href: "/products",
        affiliate: false,
      },
      {
        title: "Кухонні інструменти",
        href: "/products",
        affiliate: false,
      },
    ],
    featured: false,
    publishedAt: "2026-03-07",
    updatedAt: "2026-03-12",
    authorName: "Кухлик",
  },
] as const

export function getAllRecipes() {
  return [...recipes]
}

export function getFeaturedRecipes() {
  return recipes.filter((recipe) => recipe.featured)
}

export function getRecipeBySlug(slug: string) {
  return recipes.find((recipe) => recipe.slug === slug)
}